/*
  Admin › Integrations (31) — wave 1: ideas 2 (plain-language "Server keys are missing" callout with a
  "Copy setup note for IT" button; key names moved to Technical details), 4 (Technical details folded
  away; the environment is an amber "Sandbox · test company" badge), 6 (when connected: who connected
  which company and when, Disconnect in ⋯ with a confirmation that explains what happens) and
  8 ("What syncs" in two lines). If the status check fails, the card says so and offers Retry.
  Wave 2: ideas 1 (two integration cards with the same anatomy — QuickBooks Online and Microsoft OneDrive
  "0 of 63 projects linked"; stacked on phones) and 3 (QuickBooks setup as a 4-step checklist — server
  keys, connect, default service item, first invoice sync — with Connect inside its own step).
  Wave 3: ideas 5 (OneDrive coverage: the active projects without a folder, each with "Link folder" → its
  Files tab) and 7 (Sync activity: every invoice sync and failure, with Retry; an example while empty), plus
  "All connected services" — every service of the shared foundation with its status and the setup IT needs.
  QuickBooks Desktop (owner decision 9 Oct 2026, read only): its own card first — QbDesktopCard.jsx.
  Final QA: the activity loads once per visit and again on "Recheck status" (recheck counter), the keys are listed
  once (step 1 of the checklist, not again in the callout), and the checklist's actions stay with their step on phones.
*/
import { useEffect, useState } from 'react';
import {
  ArrowRight, CheckCircle2, ChevronDown, CircleCheck, CircleDot, CircleX, Cloud, CloudOff, Copy, Info, Link2, Loader2,
  Lock, PlugZap, ReceiptText, RefreshCw, TriangleAlert, Unplug, XCircle,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber, formatTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { ProgressBar } from '@/components/ui/progress-bar';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { AdminCallout, AdminCard, UpperLabel } from '@/components/admin/AdminUi';
import { copyText } from '@/components/admin/PasswordField';
import {
  QUICKBOOKS_KEYS, quickBooksKeyLabel, quickBooksSetupNote, quickBooksState, quickBooksSteps,
} from '@/components/admin/adminModel';
import { OneDriveUnlinked, ServicesCard, SyncActivityCard } from '@/components/admin/IntegrationsExtras';
import QbDesktopCard from '@/components/admin/QbDesktopCard';

const STATE_PILL = {
  setup: { tone: 'amber', label: 'Setup needed' },
  ready: { tone: 'gray', label: 'Not connected' },
  connected: { tone: 'green', label: 'Connected' },
};

const ONEDRIVE_KEY_LABELS = {
  MS_ONEDRIVE_CLIENT_ID: 'Client ID',
  MS_ONEDRIVE_CLIENT_SECRET: 'Client secret',
  MS_ONEDRIVE_REDIRECT_URI: 'Redirect URI',
};

function EnvironmentBadge({ environment }) {
  const sandbox = String(environment || 'sandbox').toLowerCase() !== 'production';
  return sandbox
    ? <StatusPill tone="amber" label="Sandbox · test company" size="sm" data-testid="quickbooks-environment" />
    : <StatusPill tone="green" label="Production company" size="sm" data-testid="quickbooks-environment" />;
}

const STEP_ICONS = {
  done: { Icon: CircleCheck, className: 'text-status-green' },
  blocked: { Icon: CircleX, className: 'text-status-red' },
  locked: { Icon: Lock, className: 'text-ink-4' },
  todo: { Icon: CircleDot, className: 'text-brand-ink' },
  waiting: { Icon: CircleDot, className: 'text-ink-4' },
};

/** 31 #3 — four steps, each with its state and, where it applies, its own action. */
function SetupChecklist({ status, sync, busy, onConnect, connectedByName }) {
  const { steps, done } = quickBooksSteps(status, sync);
  const detail = {
    keys: (step) => (step.state === 'done'
      ? 'All set on the server'
      : `Missing: ${step.missing.map(quickBooksKeyLabel).join(', ')}`),
    connect: (step) => (step.state === 'done'
      ? `Company ${status?.realm_id || ''}${connectedByName ? ` · by ${connectedByName}` : ''}${status?.connected_at ? ` · ${formatDate(status.connected_at)}` : ''}`
      : step.state === 'locked' ? 'Unlocks after step 1 · opens Intuit sign-in in a pop-up' : 'Opens Intuit sign-in in a pop-up'),
    item: (step) => (step.state === 'done' ? 'Every synced invoice line uses this item' : 'Set by IT on the server — every synced invoice line uses this item'),
    sync: (step) => (step.state === 'done'
      ? `${formatNumber(step.synced)} ${step.synced === 1 ? 'invoice' : 'invoices'} sent${sync?.last_synced_at ? ` · last ${formatDate(sync.last_synced_at)}${sync.last_synced_by ? ` by ${sync.last_synced_by}` : ''}` : ''}`
      : 'Invoicing › open an invoice › Sync to QuickBooks'),
  };
  const right = (step) => {
    if (step.key === 'keys') {
      return step.state === 'done'
        ? <StatusPill tone="green" label="Done" size="sm" />
        : <StatusPill tone="red" label={`${step.missing.length} missing`} size="sm" data-testid="quickbooks-keys-missing" />;
    }
    if (step.key === 'connect') {
      if (step.state === 'done') return <StatusPill tone="green" label="Connected" size="sm" />;
      return (
        <Button
          type="button"
          variant={step.state === 'todo' ? 'primary' : 'outline'}
          size="sm"
          onClick={onConnect}
          disabled={busy || step.state === 'locked'}
          title={step.state === 'locked' ? 'Unlocks once IT adds the server keys' : undefined}
          data-testid="quickbooks-connect"
        >
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Link2 aria-hidden="true" />} Connect QuickBooks
        </Button>
      );
    }
    if (step.key === 'item') {
      return step.state === 'done'
        ? <StatusPill tone="green" label="Set" size="sm" />
        : <StatusPill tone="amber" label="Not set · IT" size="sm" />;
    }
    return step.state === 'done' ? <StatusPill tone="green" label="Done" size="sm" /> : <span className="text-[13px] text-ink-4 max-sm:hidden" aria-label="Not yet">—</span>;
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <UpperLabel>Setup</UpperLabel>
        <span className="text-[12px] text-ink-3" data-testid="quickbooks-setup-progress">{done} of {steps.length} done</span>
      </div>
      <ol className="mt-1 divide-y divide-line-2" data-testid="quickbooks-setup-steps">
        {steps.map((step, index) => {
          const { Icon, className } = STEP_ICONS[step.state];
          return (
            <li key={step.key} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3" data-state={step.state} data-testid={`quickbooks-step-${step.key}`}>
              <Icon className={cn('h-[18px] w-[18px] shrink-0', className)} aria-hidden="true" />
              <div className="min-w-0 flex-1 basis-[220px]">
                <p className={cn('text-[13.5px] font-semibold', step.state === 'locked' ? 'text-ink-3' : 'text-ink')}>
                  <span className="mr-1 font-normal text-ink-3">{index + 1}</span>{step.title}
                  <span className="sr-only"> — {step.state === 'done' ? 'done' : step.state === 'locked' ? 'locked' : step.state === 'blocked' ? 'needs IT' : 'not yet'}</span>
                </p>
                <p className="text-[12px] text-ink-3">{detail[step.key](step)}</p>
              </div>
              {/* phones: the step's action wraps under its text, not off to the far right on a line of its own */}
              <div className="ml-auto shrink-0 max-sm:ml-0 max-sm:basis-full max-sm:pl-[30px]">{right(step)}</div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** 31 #1 — OneDrive: linked per project from its Files tab; here the coverage and the server keys. */
function OneDriveCard({ data, loading, unlinked, unlinkedLoading }) {
  const onedrive = data?.onedrive;
  const configured = Boolean(onedrive?.configured);
  const linked = Number(onedrive?.linked || 0);
  const total = Number(onedrive?.projects || 0);
  const pill = !onedrive
    ? null
    : !configured
      ? <StatusPill tone="amber" label="Setup needed" size="sm" data-testid="onedrive-state" />
      : linked
        ? <StatusPill tone="green" label={`${linked} linked`} size="sm" data-testid="onedrive-state" />
        : <StatusPill tone="gray" label="Not linked" size="sm" data-testid="onedrive-state" />;
  return (
    <AdminCard
      title="Microsoft OneDrive"
      subtitle="Keep each project's Files tab in sync with a OneDrive folder"
      icon={Cloud}
      iconTone="blue"
      badge={pill}
      bodyClassName="space-y-4 py-5"
      data-testid="onedrive-card"
    >
      {loading && !onedrive ? <div className="h-24 animate-pulse rounded-xl bg-surface-2" aria-label="Loading OneDrive status" /> : null}
      {!loading && !onedrive ? (
        <p className="flex items-center gap-1.5 text-[13px] text-ink-3"><CloudOff className="h-4 w-4" aria-hidden="true" /> Couldn’t read the OneDrive status — use Recheck status.</p>
      ) : null}
      {onedrive ? (
        <>
          <div>
            <p className="flex items-baseline gap-1.5" data-testid="onedrive-coverage">
              <span className="font-heading text-[28px] font-black leading-none tabular-nums text-ink">{formatNumber(linked)}</span>
              <span className="text-[13px] text-ink-3">/ {formatNumber(total)} projects linked</span>
            </p>
            <ProgressBar value={total ? (linked / total) * 100 : 0} tone="blue" label="Projects linked to OneDrive" className="mt-2.5" />
            <p className="mt-2 text-[12.5px] text-ink-3">
              OneDrive is linked per project, from the project’s Files tab, by the engineer who owns the folder.
              {onedrive.active_projects ? ` ${formatNumber(onedrive.linked_active)} of ${formatNumber(onedrive.active_projects)} active projects are linked.` : ''}
            </p>
          </div>
          <OneDriveUnlinked data={unlinked} loading={unlinkedLoading} />
          <div>
            <UpperLabel>How a project gets linked</UpperLabel>
            <ol className="mt-2 space-y-1.5 text-[12.5px] text-ink-2">
              {[
                <>Open the project › <b className="font-semibold text-ink">Files</b> tab › <b className="font-semibold text-ink">Connect OneDrive</b> — Microsoft sign-in opens in a pop-up</>,
                <>Enter the project’s folder path and save it</>,
                <><b className="font-semibold text-ink">Scan OneDrive</b> — checks the folder against the required structure</>,
              ].map((text, index) => (
                <li key={index} className="flex gap-2">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-surface-2 text-[11px] font-bold text-ink-2">{index + 1}</span>
                  <span>{text}</span>
                </li>
              ))}
            </ol>
          </div>
          {configured ? (
            <AdminCallout tone="navy" icon={Info} className="py-3" data-testid="onedrive-ready">
              <p>The server is set up for OneDrive — any project can be linked from its Files tab.</p>
            </AdminCallout>
          ) : (
            <AdminCallout tone="amber" icon={TriangleAlert} className="py-3" title="Server keys are missing" data-testid="onedrive-setup-callout">
              <p>
                Linking says “not configured” until IT adds {onedrive.missing.length} OneDrive {onedrive.missing.length === 1 ? 'key' : 'keys'}:{' '}
                {onedrive.missing.map((key) => ONEDRIVE_KEY_LABELS[key] || key).join(', ')}.
              </p>
              <p className="mt-1 font-mono text-[11.5px] text-ink-3">{onedrive.missing.join(' · ')}</p>
            </AdminCallout>
          )}
        </>
      ) : null}
    </AdminCard>
  );
}

export default function IntegrationsTab({ status, loading, error = '', users = [], appName, onRefresh, overview, overviewLoading, recheck = 0 }) {
  // Wave 3: projects without a OneDrive folder + the QuickBooks sync feed — loaded with this tab, again on
  // "Recheck status" (the page's recheck counter) or after a Retry; not every time the status object changes
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityTick, setActivityTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setActivityLoading(true);
    api.get('/admin/integrations/activity')
      .then((res) => { if (alive) setActivity(res.data || null); })
      .catch(() => { if (alive) setActivity(null); })
      .finally(() => { if (alive) setActivityLoading(false); });
    return () => { alive = false; };
  }, [recheck, activityTick]);

  const confirm = useConfirm();
  const [techOpen, setTechOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, company_name, error, checked_at }
  const state = quickBooksState(status);
  const pill = STATE_PILL[state];
  const missing = status?.missing || [];
  const missingLabels = (missing.length ? missing : QUICKBOOKS_KEYS).map(quickBooksKeyLabel);
  // The callback stores who connected as a user id — show their name, or nothing if they are gone.
  const connectedByName = status?.connected_by
    ? users.find((person) => person.id === status.connected_by || person.name === status.connected_by)?.name || ''
    : '';
  const checkFailed = !loading && !status;

  const copyNote = async () => {
    try {
      await copyText(quickBooksSetupNote({ missing, appName, origin: window.location.origin }));
      notify.success('Setup note copied', { description: 'Paste it into a message to IT.' });
    } catch (err) {
      notify.error('Could not copy the note', { description: getErrorMessage(err) });
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.get('/quickbooks/connect', { params: { frontend_origin: window.location.origin } });
      if (res.data?.authorization_url) {
        window.open(res.data.authorization_url, 'quickbooks-connect', 'width=900,height=720');
      }
    } catch (err) {
      notify.error('Could not start the QuickBooks connection', { description: getErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  // Refreshes the token and reads the company from QuickBooks (GET /quickbooks/test) — nothing is synced.
  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.get('/quickbooks/test');
      setTestResult(res.data || { ok: false, error: 'No answer from the server.' });
    } catch (err) {
      setTestResult({ ok: false, error: getErrorMessage(err, 'Could not test the connection') });
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    const ok = await confirm({
      title: 'Disconnect QuickBooks?',
      subtitle: status?.realm_id ? `Company ${status.realm_id}` : undefined,
      description: 'What happens:',
      consequences: [
        'Invoices already synced stay in QuickBooks and keep their link here.',
        'New invoices stop syncing until someone connects again.',
      ],
      tone: 'danger',
      confirmLabel: 'Disconnect',
      onConfirm: async () => { await api.post('/quickbooks/disconnect'); },
    });
    if (!ok) return;
    notify.success('QuickBooks disconnected');
    onRefresh?.();
  };

  return (
    <div className="space-y-4">
    {/* Owner decision 9 Oct 2026: the books are in QuickBooks Desktop — read only, first on the page */}
    <QbDesktopCard recheck={recheck} />
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]" data-testid="integrations-grid">
      <AdminCard
        title="QuickBooks Online"
        subtitle={`Send ${appName} invoices to your accounting company`}
        icon={ReceiptText}
        badge={(
          <>
            {status ? <StatusPill tone={pill.tone} label={pill.label} size="sm" data-testid="quickbooks-state" /> : null}
            {status && state !== 'setup' ? <EnvironmentBadge environment={status.environment} /> : null}
          </>
        )}
        aside={state === 'connected' ? (
          <RowActions
            label="QuickBooks actions"
            items={[{ label: 'Disconnect…', icon: Unplug, danger: true, onSelect: disconnect, testId: 'quickbooks-disconnect' }]}
          />
        ) : null}
        bodyClassName="space-y-5 py-5"
        data-testid="quickbooks-card"
      >
        {loading && !status ? (
          <div className="h-24 animate-pulse rounded-xl bg-surface-2" aria-label="Loading QuickBooks status" />
        ) : null}

        {checkFailed ? (
          <EmptyState
            layout="inline"
            tone="red"
            icon={CloudOff}
            title="Couldn’t check QuickBooks"
            titleAs="h3"
            text={`${error ? `${error.replace(/[.\s]+$/, '')}. ` : ''}Invoices keep working — only the connection status is unknown.`}
            action={(
              <Button type="button" variant="outline" size="sm" onClick={onRefresh} data-testid="quickbooks-status-retry">
                <RefreshCw aria-hidden="true" /> Retry
              </Button>
            )}
            className="rounded-xl border border-[color:var(--danger-border)] bg-status-red-bg"
            data-testid="quickbooks-status-error"
          />
        ) : null}

        {state === 'setup' && status ? (
          <AdminCallout
            tone="amber"
            icon={TriangleAlert}
            title="Server keys are missing"
            data-testid="quickbooks-setup-callout"
          >
            <p>
              IT needs to add {missingLabels.length} QuickBooks {missingLabels.length === 1 ? 'key' : 'keys'} on the server before anyone can connect.
              Invoices keep working in {appName} — they just don’t reach QuickBooks yet.
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-3 bg-surface" onClick={copyNote} data-testid="quickbooks-copy-setup-note">
              <Copy aria-hidden="true" /> Copy setup note for IT
            </Button>
          </AdminCallout>
        ) : null}

        {status ? (
          <SetupChecklist status={status} sync={overview?.quickbooks_sync} busy={busy} onConnect={connect} connectedByName={connectedByName} />
        ) : null}

        {state === 'connected' ? (
          <div className="rounded-xl border border-line bg-surface-2 p-4" data-testid="quickbooks-connection">
            <p className="text-[13.5px] text-ink">
              Connected to company <span className="font-mono text-[12.5px]">{status.realm_id}</span>
              {connectedByName ? <> by <b className="font-semibold">{connectedByName}</b></> : null}
              {status.connected_at ? <> on {formatDate(status.connected_at)}</> : null}
            </p>
            {!status.has_default_item ? (
              <p className="mt-1.5 text-[12.5px] font-semibold text-status-amber-ink">
                No default service item is set — invoice sync needs one (ask IT for QUICKBOOKS_DEFAULT_ITEM_ID).
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <Button type="button" variant="outline" size="sm" className="bg-surface" onClick={testConnection} disabled={testing} data-testid="quickbooks-test">
                {testing ? <Loader2 className="animate-spin" aria-hidden="true" /> : <PlugZap aria-hidden="true" />}
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={connect} disabled={busy} data-testid="quickbooks-reconnect">
                <Link2 aria-hidden="true" /> Reconnect
              </Button>
              {testResult && !testing ? (
                <p
                  role="status"
                  className={cn('inline-flex min-w-0 items-center gap-1.5 text-[12.5px] font-semibold', testResult.ok ? 'text-status-green-ink' : 'text-status-red-ink')}
                  data-testid="quickbooks-test-result"
                >
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> : <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  <span className="min-w-0">
                    {testResult.ok
                      ? `Connection works${testResult.company_name ? ` · ${testResult.company_name}` : ''}`
                      : testResult.error || 'The connection does not work.'}
                    {testResult.checked_at ? <span className="font-normal text-ink-3"> · checked {formatTime(testResult.checked_at)}</span> : null}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div>
          <UpperLabel>What syncs</UpperLabel>
          <ul className="mt-2 divide-y divide-line-2 rounded-xl border border-line text-[13px]" data-testid="quickbooks-what-syncs">
            {[
              [`${appName} invoices & variation invoices`, 'QuickBooks invoice'],
              ['Project client', 'QuickBooks customer (created if missing)'],
            ].map(([from, to]) => (
              <li key={from} className="grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] items-center gap-2 px-3 py-2.5">
                <span className="font-semibold text-ink">{from}</span>
                <ArrowRight className="h-3.5 w-3.5 text-ink-3" aria-label="becomes" />
                <span className="text-ink-2">{to}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setTechOpen((current) => !current)}
            aria-expanded={techOpen}
            aria-controls="quickbooks-technical"
            className="inline-flex items-center gap-1.5 rounded text-[13px] font-semibold text-ink hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="quickbooks-technical-toggle"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !techOpen && '-rotate-90')} aria-hidden="true" />
            Technical details
          </button>
          {techOpen ? (
            <div id="quickbooks-technical" className="mt-2 rounded-xl border border-line bg-surface-2 p-3.5" data-testid="quickbooks-technical">
              <dl className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-6">
                <div>
                  <dt className="text-[12px] text-ink-3">Environment</dt>
                  <dd className="mt-1"><EnvironmentBadge environment={status?.environment} /></dd>
                </div>
                <div>
                  <dt className="text-[12px] text-ink-3">Company realm</dt>
                  <dd className="mt-1 break-all text-[13px] text-ink">{status?.realm_id ? <span className="font-mono">{status.realm_id}</span> : '—'}</dd>
                </div>
                <div>
                  <dt className="text-[12px] text-ink-3">API minor version</dt>
                  <dd className="mt-1 font-mono text-[13px] text-ink">{status?.minor_version || '—'}</dd>
                </div>
              </dl>
              <div className="mt-3 border-t border-line pt-3">
                <p className="text-[12px] text-ink-3">Server keys</p>
                <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11.5px]">
                  {QUICKBOOKS_KEYS.map((key) => (
                    <li key={key} className={missing.includes(key) ? 'text-status-red-ink' : 'text-ink-3'}>
                      {key}{missing.includes(key) ? ' · missing' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </AdminCard>

      <OneDriveCard
        data={overview}
        loading={overviewLoading}
        unlinked={activity?.onedrive_unlinked}
        unlinkedLoading={activityLoading}
      />
    </div>
    <SyncActivityCard
      activity={activity?.quickbooks_activity}
      dailySummary={activity?.daily_summary}
      loading={activityLoading}
      connected={quickBooksState(status) === 'connected'}
      onRetried={() => { setActivityTick((n) => n + 1); onRefresh?.(); }}
    />
    <ServicesCard appName={appName} recheck={recheck} />
    </div>
  );
}
